import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEnvelope,
  faPhone,
  faGlobe,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { 
  faSpotify, 
  faApple, 
  faSoundcloud, 
  faYoutube, 
  faInstagram, 
  faFacebook, 
  faTwitter, 
  faTiktok,
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import styles from './EPKBuilder.module.css';

const ContactStep = ({ data, updateData, errors, mode }) => {
  console.log('ContactStep rendering with data:', data);
  console.log('Errors:', errors);

  // Initialize state from props correctly
  const [contact, setContact] = useState(() => ({
    name: data.contact?.name || '',
    role: data.contact?.role || '',
    email: data.contact?.email || '',
    phone: data.contact?.phone || '',
    website: data.contact?.website || ''
  }));
  
  const [socialLinks, setSocialLinks] = useState(() => ({
    spotify: data.socialLinks?.spotify || '',
    appleMusic: data.socialLinks?.appleMusic || '',
    instagram: data.socialLinks?.instagram || '',
    facebook: data.socialLinks?.facebook || '',
    twitter: data.socialLinks?.twitter || '',
    tiktok: data.socialLinks?.tiktok || '',
    youtube: data.socialLinks?.youtube || '',
    soundcloud: data.socialLinks?.soundcloud || '',
    linkedin: data.socialLinks?.linkedin || ''
  }));
  
  // Use ref to track initial render and prevent update loops
  const initialRender = useRef(true);
  const prevContactRef = useRef(contact);
  const prevSocialLinksRef = useRef(socialLinks);
  
  // Sync with parent component only when data actually changes
  useEffect(() => {
    // Skip the update on the initial render
    if (initialRender.current) {
      initialRender.current = false;
      prevContactRef.current = contact;
      prevSocialLinksRef.current = socialLinks;
      return;
    }
    
    // Only update if data has actually changed
    if (JSON.stringify(prevContactRef.current) !== JSON.stringify(contact) || 
        JSON.stringify(prevSocialLinksRef.current) !== JSON.stringify(socialLinks)) {
      
      console.log('ContactStep: Syncing with parent component', { contact, socialLinks });
      updateData({
        contact,
        socialLinks
      });
      
      // Update refs
      prevContactRef.current = contact;
      prevSocialLinksRef.current = socialLinks;
    }
  }, [contact, socialLinks, updateData]);
  
  // Debug log for monitoring contact email state
  useEffect(() => {
    console.log('Current contact email value:', contact.email);
  }, [contact.email]);
  
  // Handle contact input change
  const handleContactChange = (field, value) => {
    if (mode === 'view') return;
    
    console.log(`ContactStep: Updating ${field} to`, value);
    setContact(prev => {
      const newState = {
        ...prev,
        [field]: value
      };
      
      // Immediately sync critical fields with parent
      if (field === 'email') {
        console.log('Email updated, immediately syncing with parent');
        updateData({
          contact: newState,
          socialLinks
        });
      }
      
      return newState;
    });
  };
  
  // Handle social link input change
  const handleSocialLinkChange = (platform, value) => {
    if (mode === 'view') return;
    
    setSocialLinks(prev => ({
      ...prev,
      [platform]: value
    }));
  };
  
  return (
    <div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
      <h2 className={styles.sectionTitle}>Contact Information</h2>
      <p className={styles.sectionDescription}>
        Provide contact details for booking inquiries and press opportunities.
      </p>
      
      {/* Primary Contact Section */}
      <div className={styles.contactSection}>
        <h3>Primary Contact</h3>
        <p className={styles.sectionSubdescription}>
          This is the main point of contact for booking and media inquiries.
        </p>
        
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="contactName">Contact Name</label>
            <input
              type="text"
              id="contactName"
              value={contact.name}
              onChange={(e) => handleContactChange('name', e.target.value)}
              placeholder="Name of manager, booking agent, or yourself"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="contactRole">Role</label>
            <select
              id="contactRole"
              value={contact.role}
              onChange={(e) => handleContactChange('role', e.target.value)}
              disabled={mode === 'view'}
            >
              <option value="">Select Role</option>
              <option value="Artist">Artist</option>
              <option value="Manager">Manager</option>
              <option value="Booking Agent">Booking Agent</option>
              <option value="PR Representative">PR Representative</option>
              <option value="Label Representative">Label Representative</option>
              <option value="Band Member">Band Member</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="contactEmail">Email *</label>
          <input
            type="email"
            id="contactEmail"
            value={contact.email}
            onChange={(e) => handleContactChange('email', e.target.value)}
            placeholder="Email address for inquiries"
            required
            disabled={mode === 'view'}
            className={errors && errors.email ? styles.inputError : ''}
          />
          {errors && errors.email && (
            <span className={styles.inputError}>{errors.email}</span>
          )}
        </div>
        
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="contactPhone">Phone (optional)</label>
            <input
              type="tel"
              id="contactPhone"
              value={contact.phone}
              onChange={(e) => handleContactChange('phone', e.target.value)}
              placeholder="Phone number with country code"
              disabled={mode === 'view'}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="contactWebsite">Website (optional)</label>
            <input
              type="url"
              id="contactWebsite"
              value={contact.website}
              onChange={(e) => handleContactChange('website', e.target.value)}
              placeholder="Your website or Linktree URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
      </div>
      
      
      {/* Social Links Section */}
      <div className={styles.socialLinksSection}>
        <h3>Social Media & Streaming Links</h3>
        <p className={styles.sectionSubdescription}>
          Add your social media and streaming platform links for easy access.
        </p>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faSpotify} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.spotify}
              onChange={(e) => handleSocialLinkChange('spotify', e.target.value)}
              placeholder="Spotify Artist URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faApple} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.appleMusic}
              onChange={(e) => handleSocialLinkChange('appleMusic', e.target.value)}
              placeholder="Apple Music Artist URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faInstagram} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.instagram}
              onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
              placeholder="Instagram Profile URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faFacebook} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.facebook}
              onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
              placeholder="Facebook Page URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faTwitter} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.twitter}
              onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
              placeholder="Twitter Profile URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faTiktok} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.tiktok}
              onChange={(e) => handleSocialLinkChange('tiktok', e.target.value)}
              placeholder="TikTok Profile URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faYoutube} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.youtube}
              onChange={(e) => handleSocialLinkChange('youtube', e.target.value)}
              placeholder="YouTube Channel URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faSoundcloud} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.soundcloud}
              onChange={(e) => handleSocialLinkChange('soundcloud', e.target.value)}
              placeholder="SoundCloud Profile URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
        
        <div className={styles.socialLinkItem}>
          <div className={styles.socialIcon}>
            <FontAwesomeIcon icon={faLinkedin} />
          </div>
          <div className={styles.socialInput}>
            <input
              type="url"
              value={socialLinks.linkedin}
              onChange={(e) => handleSocialLinkChange('linkedin', e.target.value)}
              placeholder="LinkedIn Profile URL"
              disabled={mode === 'view'}
            />
          </div>
        </div>
      </div>
      
      {mode !== 'view' && (
        <div className={styles.tip}>
          <FontAwesomeIcon icon={faInfoCircle} className={styles.tipIcon} />
          <p>
            Tip: Including your social media links makes it easy for promoters to verify your online presence and audience engagement.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactStep;